/**
 * Ordering for the Elite Pulse / RTL scanner board.
 *
 * The board defaults to descending, which is what made the previous implementation look
 * broken: it represented "no value" as the sentinel `1e9`, so every row missing a premium
 * or a relative volume compared as the *largest* number and floated to the top of exactly
 * the sort a user reaches for to find the biggest flow. Missing values are `null` here and
 * are held at the bottom by the comparator, in both directions.
 */

import type { ScannerTile } from './rtlScannerTiles';

/** Buy / sell / unknown, from flow direction and option side. Drives colour and `fl` ordering. */
export function tileFlowClass(r: ScannerTile): string {
  if (r.flow !== 'BUY' && r.flow !== 'SELL') return 'unk';
  return (r.flow === 'BUY' && r.side === 'C') || (r.flow === 'SELL' && r.side === 'P') ? 'buy' : 'sell';
}

const FLOW_ORDER: Record<string, number> = { buy: 0, sell: 1, unk: 2 };
const MONEYNESS_ORDER: Record<string, number> = { ITM: 0, ATM: 1, OTM: 2, NA: 3 };
const HORIZON_ORDER: Record<string, number> = { '[L]': 0, '[M]': 1, '[T]': 2 };

// Ordered so one click groups the board by conviction: bought together, sold together,
// complex orders above lone ones, and provable opens above the rows that cannot say.
const DIRECTION_ORDER: Record<string, number> = { Bought: 0, 'Two-sided': 1, Sold: 2 };
const STRUCTURE_ORDER: Record<string, number> = { 'Multi-leg': 0, Mixed: 1, 'Single-leg': 2 };
// Opening first, then the weaker "likely", then flat, then closing: one click groups the
// board by whether money is being committed or taken off the table.
const POSITION_ORDER: Record<string, number> = {
  Opening: 0,
  'Likely opening': 1,
  Flat: 2,
  Indeterminate: 3,
  Closing: 4,
};

/**
 * The value a tile sorts on for `key`, or `null` when the tile has no value for it.
 *
 * `null` rather than a sentinel number. A sentinel has to be either very large or very
 * small, and whichever is chosen it lands at the wrong end for one of the two sort
 * directions; only a value the comparator can recognise as absent works for both.
 */
export function scannerTileSortValue(r: ScannerTile, key: string): number | string | null {
  if (key.startsWith('raw:')) {
    const v = r.raw?.[key.slice(4)];
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (v == null || v === '') return null;
    return String(v);
  }

  switch (key) {
    case 't':
      return r.t || null;
    case 'fl':
      return FLOW_ORDER[tileFlowClass(r)] ?? null;
    case 'money':
      return MONEYNESS_ORDER[r.mc] ?? null;
    case 'side':
      return r.side === 'C' ? 0 : r.side === 'P' ? 1 : 2;
    case 'horizon':
      return r.horizon_tml ? HORIZON_ORDER[r.horizon_tml] ?? null : null;
    case 'exp':
    case 'dte':
      return r.dte == null ? null : r.dte;
    case 'conv': {
      // Ordered by strength so one click brings the strongest convictions to the top.
      const rank: Record<string, number> = { HIGH: 0, ELEVATED: 1, MODERATE: 2 };
      return r.conviction ? rank[r.conviction] ?? null : null;
    }
    case 'flags':
      // Descending puts the most corroborated contracts first, which is the reason to sort by it.
      return r.appearances ?? r.sources?.length ?? null;
    case 'vdir':
      return r.verdict?.direction ? DIRECTION_ORDER[r.verdict.direction] ?? null : null;
    case 'vstruct':
      return r.verdict?.structure ? STRUCTURE_ORDER[r.verdict.structure] ?? null : null;
    case 'vpos':
      return r.verdict?.position ? POSITION_ORDER[r.verdict.position] ?? null : null;
    default: {
      const v = (r as unknown as Record<string, unknown>)[key];
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
  }
}

/**
 * Total order over tiles for one column and direction.
 *
 * Three rules the previous comparator did not have:
 *
 *  - absent values sort last whichever way the arrow points, because "show me the biggest"
 *    and "show me the smallest" both mean "among the rows that have one";
 *  - numbers compare numerically and text compares with `numeric: true`, so a column
 *    holding `9` and `26` never orders them as the strings `"26" < "9"`;
 *  - ties break on the tile id, so the board does not reshuffle equal rows on every poll.
 */
export function compareScannerTiles(
  a: ScannerTile,
  b: ScannerTile,
  key: string,
  dir: 1 | -1,
): number {
  const x = scannerTileSortValue(a, key);
  const y = scannerTileSortValue(b, key);

  if (x === null || y === null) {
    if (x === null && y === null) return a.id.localeCompare(b.id);
    return x === null ? 1 : -1;
  }

  let cmp: number;
  if (typeof x === 'number' && typeof y === 'number') {
    cmp = x - y;
  } else if (typeof x === 'number') {
    cmp = -1;
  } else if (typeof y === 'number') {
    cmp = 1;
  } else {
    cmp = x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' });
  }

  // Tie-break outside the direction flip: a stable order is not a reversible one.
  if (cmp === 0) return a.id.localeCompare(b.id);
  return cmp * dir;
}

/**
 * A number typed into a filter box, or null when the box is empty or unusable.
 *
 * Strips thousands separators before parsing: `parseFloat('1,000')` is `1`, so a user
 * asking for a million dollars of premium silently got a filter at one dollar.
 */
export function parseFilterNumber(raw: string): number | null {
  const cleaned = String(raw ?? '').replace(/[,\s$]/g, '').trim();
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}
